import { InventoryItem } from '../redux/inventorySlice';
import { RecipeIngredient } from '../types/recipe';
import { ingredientMatchKey, ingredientMatchText } from './inventoryMatchService';
import {
  buildPantryIndex,
  coverageForLine,
  type CoverageResult,
  type CoverageDetail,
  type LearnedCoverage,
} from './ingredientCoverageService';

/**
 * Pantry status of a recipe ingredient, derived (never user-entered) by crossing
 * the recipe with the inventory. The pantry is the source of truth: correcting a
 * status means updating the pantry, not the grocery list.
 *
 * 'staple' is displayed as "have" everywhere; only 'buy' feeds the list.
 *
 * 'probably' is the state this screen used to lack, and lacking it is what made
 * it wrong. Its matcher counted any whole word shared with a pantry row, so five
 * limes covered "kaffir lime leaves" and a tub of butter covered "peanut
 * butter" — silently, with a green check. Now an uncertain match says so and the
 * user settles it in one tap.
 *
 * Both this screen and the grocery list now derive from
 * `ingredientCoverageService`, so they cannot reach opposite conclusions about
 * the same line, which they previously did for "2 tbsp fresh lime juice".
 */
export type IngredientPantryStatus = 'have' | 'probably' | 'buy' | 'staple';

export interface IngredientInventoryMatch {
  inventoryItemId: string;
  inventoryItemName: string;
}

export interface IngredientStatusResult {
  /** Parallel to the input ingredients array. */
  statuses: IngredientPantryStatus[];
  /** Matched inventory item per ingredient (null when staple or buy). */
  matches: (IngredientInventoryMatch | null)[];
  /**
   * The numbers behind a 'probably' row, for the UI to phrase in the user's
   * language. Null on every other state. Parallel array.
   */
  details: (CoverageDetail | null)[];
  /**
   * For a 'probably' row, the two canonical ids the question is about:
   * `have` is what the pantry holds, `need` is what the recipe wants. The
   * answer is stored against this pair, so it survives the row being reworded.
   */
  askedAbout: ({ have: string; need: string } | null)[];
  /** have counts staples and 'probably'; buy is what "Add the N" adds. */
  coverage: { have: number; buy: number; total: number };
}

/** The canonical id a pantry row resolved to, for storing the user's answer. */
function pantryIdOf(
  index: ReturnType<typeof buildPantryIndex>,
  itemId: string
): string | null {
  for (const [id, items] of index.byId.entries()) {
    if (items.some(item => item.id === itemId)) return id;
  }
  return null;
}

export function deriveIngredientStatuses(
  ingredients: RecipeIngredient[],
  inventory: InventoryItem[],
  learned?: LearnedCoverage,
  /** Basiques the cook turned off, as on the grocery list. */
  excludedStaples: readonly string[] = []
): IngredientStatusResult {
  const index = buildPantryIndex(
    inventory as unknown as Parameters<typeof buildPantryIndex>[0],
    learned,
    excludedStaples
  );

  const statuses: IngredientPantryStatus[] = [];
  const matches: (IngredientInventoryMatch | null)[] = [];
  const details: (CoverageDetail | null)[] = [];
  const askedAbout: ({ have: string; need: string } | null)[] = [];
  let buy = 0;

  for (const ingredient of ingredients) {
    // Resolve from `original || name`, not from the lookup key. The scraper's
    // `name` field is regularly the more damaged of the two — "1 pound thin,
    // round rice noodles" arrives with name = "thin" — and reducing that answers
    // a different question from the one the row displays (#601).
    const result: CoverageResult = coverageForLine(ingredientMatchText(ingredient), index);
    const fallbackName = ingredientMatchKey(ingredient);

    const source = result.via[0];

    statuses.push(result.state);
    details.push(result.state === 'probably' ? result.detail ?? null : null);
    askedAbout.push(
      result.state === 'probably' && source && result.ref.canonicalId
        ? { have: pantryIdOf(index, source.itemId) ?? '', need: result.ref.canonicalId }
        : null
    );

    matches.push(
      source
        ? { inventoryItemId: source.itemId, inventoryItemName: source.itemName || fallbackName }
        : null
    );

    if (result.state === 'buy') buy++;
  }

  return {
    statuses,
    matches,
    details,
    askedAbout,
    coverage: { have: ingredients.length - buy, buy, total: ingredients.length },
  };
}

/**
 * What one row will read once a pantry row is gone (#805).
 *
 * A row names only the first pantry row that covers it. « Je n'en ai plus »
 * removed that one, the next tomato row took over, and the check never moved:
 * the cook tapped, saw nothing change, and could not tell why. The screen asks
 * this before closing the strip — still covered means it names the next row and
 * asks about that one; to buy means the answer landed.
 */
export function statusAfterRemoving(
  ingredient: RecipeIngredient,
  inventory: InventoryItem[],
  removedItemId: string,
  learned?: LearnedCoverage,
  excludedStaples: readonly string[] = []
): { status: IngredientPantryStatus; match: IngredientInventoryMatch | null } {
  const result = deriveIngredientStatuses(
    [ingredient],
    inventory.filter(item => item.id !== removedItemId),
    learned,
    excludedStaples
  );
  return { status: result.statuses[0], match: result.matches[0] };
}
